import { useState } from "react";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

/**
 * A time picker component built on top of MUI's TimePicker with additional customization options.
 *
 * @param {Object} props - The component props
 * @param {moment.Moment|null} props.value - The selected time value as a Moment object
 * @param {function} props.onChange - Callback function called when the time value changes
 * @param {string} [props.label] - Label text displayed above the input field
 * @param {string} [props.name] - Name attribute for the input field, used for generating test IDs
 * @param {Object} [props.error] - Error object with a message property to display validation errors
 * @param {string|null} [props.helperText=null] - Helper text displayed below the input field when no error is present
 * @param {boolean} [props.disabled] - Whether the time picker is disabled
 * @param {Object} [props.sx={}] - Custom styles object for the text field component
 * @param {string} [props.className] - CSS class name to apply to the text field
 * @param {boolean} [props.required] - Whether the time picker is required
 * @param {string} [props.format="HH:mm"] - Time format string (e.g., "HH:mm", "hh:mm A")
 * @param {function} [props.onBlur] - Callback function called when the input field loses focus
 * @param {boolean} [props.ampm] - Whether to display time in 12-hour format with AM/PM
 * @param {Object} [props.props] - Additional props passed to the underlying TimePicker component
 * @param {boolean} [props.shrink] - Whether to shrink the input label
 * @returns {JSX.Element} The rendered MiTimePicker component
 */
export function MiTimePicker({
  value,
  onChange,
  label,
  name,
  error,
  helperText = null,
  disabled,
  sx = {},
  className,
  required,
  format = "HH:mm",
  onBlur,
  ampm,
  shrink,
  strictInterval,
  minutesStep,
  ...props
}) {
  const [open, setOpen] = useState(false);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <TimePicker
        label={label}
        value={value}
        required={required}
        format={format}
        onChange={onChange}
        disabled={disabled}
        error={!!error}
        helperText={error ? error.message : helperText}
        ampm={ampm}
        minutesStep={minutesStep}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            error: !!error,
            helperText: error ? error.message : helperText,
            className,
            required,
            onBlur,
            InputLabelProps: {
              shrink,
            },
            inputProps: {
              "data-testid": `mi-time-picker-${name}`,
              readOnly: !!strictInterval,
              style: { cursor: "pointer" },
              onClick: () => {
                if (strictInterval) {
                  setOpen(true);
                }
              },
            },
            sx: {
              ...sx,
            },
          },
        }}
        {...props}
      />
    </LocalizationProvider>
  );
}
